import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Shared request helper: every endpoint is /api/workspaces/:workspaceId/contact-profile-fields[/...].
async function request(workspaceId, path, { method = "GET", body, fallbackMessage }) {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE}/api/workspaces/${workspaceId}/contact-profile-fields${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || fallbackMessage);
  return data;
}

export const fetchContactProfileFields = createAsyncThunk(
  "contactProfileFields/fetch",
  async (workspaceId, { rejectWithValue }) => {
    try {
      const data = await request(workspaceId, "", { fallbackMessage: "Failed to load profile fields" });
      return data.fields;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createContactProfileField = createAsyncThunk(
  "contactProfileFields/create",
  async ({ workspaceId, name, type, showInInbox }, { rejectWithValue }) => {
    try {
      const data = await request(workspaceId, "", {
        method: "POST",
        body: { name, type, showInInbox },
        fallbackMessage: "Failed to create profile field",
      });
      return data.field;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateContactProfileField = createAsyncThunk(
  "contactProfileFields/update",
  async ({ workspaceId, fieldId, changes }, { rejectWithValue }) => {
    try {
      const data = await request(workspaceId, `/${fieldId}`, {
        method: "PUT",
        body: changes,
        fallbackMessage: "Failed to update profile field",
      });
      return data.field;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteContactProfileField = createAsyncThunk(
  "contactProfileFields/delete",
  async ({ workspaceId, fieldId }, { rejectWithValue }) => {
    try {
      await request(workspaceId, `/${fieldId}`, { method: "DELETE", fallbackMessage: "Failed to delete profile field" });
      return fieldId;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Local order is applied optimistically (see setContactProfileFieldsOrder); on failure the
// caller re-fetches to restore the server's order.
export const saveContactProfileFieldsOrder = createAsyncThunk(
  "contactProfileFields/reorder",
  async ({ workspaceId, orderedIds }, { rejectWithValue }) => {
    try {
      await request(workspaceId, "/reorder", {
        method: "PUT",
        body: { orderedIds },
        fallbackMessage: "Failed to save field order",
      });
      return orderedIds;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const contactProfileFieldsSlice = createSlice({
  name: "contactProfileFields",
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {
    setContactProfileFieldsOrder(state, action) {
      const byId = new Map(state.items.map((f) => [f._id, f]));
      state.items = action.payload.map((id) => byId.get(id)).filter(Boolean);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContactProfileFields.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchContactProfileFields.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(fetchContactProfileFields.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createContactProfileField.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateContactProfileField.fulfilled, (state, action) => {
        state.items = state.items.map((f) => (f._id === action.payload._id ? action.payload : f));
      })
      .addCase(deleteContactProfileField.fulfilled, (state, action) => {
        state.items = state.items.filter((f) => f._id !== action.payload);
      });
  },
});

export const { setContactProfileFieldsOrder } = contactProfileFieldsSlice.actions;
export default contactProfileFieldsSlice.reducer;
